import React, { useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    useTheme
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { DrawerHeader } from "@/components";
import Grid from "@mui/material/Grid2"
import { getYoutubeThumbnailUrl, getYouTubeVideoId } from "@/utils/formattingFunctions";
import { IVideo } from "@/interfaces";
import { videos } from "@/utils/data-provider/data";

const VideoList: React.FC = () => {
    const theme = useTheme();
    const [selectedVideo, setSelectedVideo] = useState<IVideo | null>(null);
    const [open, setOpen] = useState(false);

    const handleOpenVideo = (video: IVideo) => {
        setSelectedVideo(video);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedVideo(null);
    };

    return (
        <Box>
            <Typography variant="h5" mb={3}>
                Video Library
            </Typography>

            <Grid container spacing={3}>
                {videos.map((video) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={video.youtubeUrl}>

                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                    cursor: 'pointer',
                                    '& .play-icon': {
                                        opacity: 1,
                                    }
                                }
                            }}
                            onClick={() => handleOpenVideo(video)}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    component="img"
                                    src={getYoutubeThumbnailUrl(video.youtubeUrl)}
                                    alt={video.title}
                                    sx={{
                                        width: '100%',
                                        height: 180,
                                        objectFit: 'cover',
                                    }}
                                />
                                <Box
                                    className="play-icon"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0,0,0,0.4)',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                    }}
                                >
                                    <PlayArrow sx={{ fontSize: 50, color: 'white' }} />
                                </Box>
                            </Box>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography gutterBottom variant="h6" component="div" noWrap>
                                    {video.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {video.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: "900px",
                            bgcolor: theme.palette.background.paper,
                            borderRadius: 1,
                        }
                    }
                }}
            >
                <DrawerHeader onCloseClick={handleClose} />
                <DialogContent>
                    {selectedVideo && (
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ position: 'relative', paddingTop: '56.25%', height: 0 }}>
                                <Box
                                    component={"iframe"}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedVideo.youtubeUrl)}`}
                                    title={selectedVideo.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </Box>
                            <Box mt={2}>
                                <Typography variant="h6">{selectedVideo.title}</Typography>
                                <Typography variant="body1" color="text.secondary" mt={1}>
                                    {selectedVideo.description}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default VideoList;